import { useState, type SubmitEvent } from 'react'
import { useTranslation } from 'react-i18next'

import { ApiError } from '@/api/problemDetails'
import { Alert, Badge, Button, toast } from '@/components/ui'
import { copyToClipboard } from '@/lib/clipboard'
import { formatDate, translateEnumValue } from '@/lib/formatting'

import { useCreateInvitation, useRevokeInvitation, useTeamInvitations } from '../queries'
import { MEMBER_ROLES, type CreatedInvitation } from '../types'

interface InviteSectionProps {
  readonly tenantId: string
}

/** Convidar por e-mail: form + link do convite (copiável, mostrado uma vez) + convites pendentes. */
export function InviteSection({ tenantId }: InviteSectionProps) {
  const { t, i18n } = useTranslation()
  const [email, setEmail] = useState('')
  const [role, setRole] = useState<string>('Agent')
  const [created, setCreated] = useState<CreatedInvitation | null>(null)

  const createInvitation = useCreateInvitation(tenantId)
  const revokeInvitation = useRevokeInvitation(tenantId)
  const invitationsQuery = useTeamInvitations(tenantId, true)

  const roleLabels: Readonly<Record<string, string>> = {
    Owner: t('team.roles.Owner'),
    Administrator: t('team.roles.Administrator'),
    Supervisor: t('team.roles.Supervisor'),
    Agent: t('team.roles.Agent'),
    Analyst: t('team.roles.Analyst'),
  }

  const submit = (event: SubmitEvent<HTMLFormElement>) => {
    event.preventDefault()
    if (email.trim().length === 0 || createInvitation.isPending) {
      return
    }
    createInvitation.mutate(
      { email: email.trim(), role },
      {
        onSuccess: (result) => {
          setCreated(result)
          setEmail('')
          toast.success(t('team.toast.invited', { email: result.email }))
        },
        onError: (error) => {
          toast.error(error instanceof ApiError ? error.problem.title : t('team.actionFailed'))
        },
      },
    )
  }

  const revoke = (invitationId: string) => {
    revokeInvitation.mutate(
      { invitationId },
      {
        onSuccess: () => {
          toast.success(t('team.toast.invitationRevoked'))
        },
        onError: (error) => {
          toast.error(error instanceof ApiError ? error.problem.title : t('team.actionFailed'))
        },
      },
    )
  }

  const pending = invitationsQuery.data ?? []

  return (
    <div className="flex flex-col gap-4">
      {created !== null ? (
        <InviteLinkReveal
          invitation={created}
          onDismiss={() => {
            setCreated(null)
          }}
        />
      ) : null}

      <form
        onSubmit={submit}
        className="flex flex-col gap-3 rounded-lg border border-border-default bg-background-elevated p-5"
      >
        <div>
          <h2 className="text-sm font-semibold text-foreground-primary">
            {t('team.invite.title')}
          </h2>
          <p className="text-sm text-foreground-muted">{t('team.invite.subtitle')}</p>
        </div>
        <div className="flex flex-col gap-3 sm:flex-row sm:items-end">
          <label className="flex flex-1 flex-col gap-1.5">
            <span className="text-xs font-medium text-foreground-secondary">
              {t('team.invite.emailLabel')}
            </span>
            <input
              type="email"
              value={email}
              onChange={(event) => {
                setEmail(event.target.value)
              }}
              placeholder={t('team.invite.emailPlaceholder')}
              className="rounded-md border border-border-default bg-background-default px-3 py-2 text-sm text-foreground-primary outline-none focus:border-action-primary"
            />
          </label>
          <label className="flex flex-col gap-1.5">
            <span className="text-xs font-medium text-foreground-secondary">
              {t('team.roleLabel')}
            </span>
            <select
              value={role}
              onChange={(event) => {
                setRole(event.target.value)
              }}
              className="rounded-md border border-border-default bg-background-default px-3 py-2 text-sm text-foreground-primary outline-none focus:border-action-primary"
            >
              {MEMBER_ROLES.map((value) => (
                <option key={value} value={value}>
                  {translateEnumValue(roleLabels, value)}
                </option>
              ))}
            </select>
          </label>
          <Button type="submit" disabled={email.trim().length === 0 || createInvitation.isPending}>
            {createInvitation.isPending ? t('team.invite.submitting') : t('team.invite.submit')}
          </Button>
        </div>
      </form>

      {pending.length > 0 ? (
        <div className="flex flex-col gap-2 rounded-lg border border-border-default bg-background-elevated p-5">
          <h3 className="text-sm font-semibold text-foreground-primary">
            {t('team.pending.title')}
          </h3>
          <ul className="flex flex-col divide-y divide-border-default">
            {pending.map((invitation) => (
              <li key={invitation.id} className="flex items-center justify-between gap-3 py-2">
                <div className="min-w-0">
                  <span className="block truncate text-sm text-foreground-primary">
                    {invitation.email}
                  </span>
                  <span className="text-xs text-foreground-muted">
                    {translateEnumValue(roleLabels, invitation.role)} ·{' '}
                    {invitation.expired
                      ? t('team.pending.expired')
                      : t('team.pending.expiresAt', {
                          date: formatDate(invitation.expiresAt, i18n.language),
                        })}
                  </span>
                </div>
                <div className="flex items-center gap-2">
                  {invitation.expired ? (
                    <Badge variant="warning">{t('team.pending.expired')}</Badge>
                  ) : null}
                  <Button
                    variant="ghost"
                    size="sm"
                    disabled={revokeInvitation.isPending}
                    onClick={() => {
                      revoke(invitation.id)
                    }}
                  >
                    {t('team.pending.revoke')}
                  </Button>
                </div>
              </li>
            ))}
          </ul>
        </div>
      ) : null}
    </div>
  )
}

function InviteLinkReveal({
  invitation,
  onDismiss,
}: {
  readonly invitation: CreatedInvitation
  readonly onDismiss: () => void
}) {
  const { t } = useTranslation()
  const [copyState, setCopyState] = useState<'idle' | 'copied' | 'failed'>('idle')

  const copy = () => {
    void copyToClipboard(invitation.inviteLink).then((ok) => {
      setCopyState(ok ? 'copied' : 'failed')
      window.setTimeout(
        () => {
          setCopyState('idle')
        },
        ok ? 1500 : 2500,
      )
    })
  }

  return (
    <div className="flex flex-col gap-3 rounded-lg border border-border-default bg-background-subtle p-5">
      <div className="flex items-start justify-between gap-3">
        <div>
          <h3 className="text-sm font-semibold text-foreground-primary">{t('team.link.title')}</h3>
          <p className="text-sm text-foreground-muted">
            {t('team.link.subtitle', { email: invitation.email })}
          </p>
        </div>
        <Button variant="ghost" size="sm" onClick={onDismiss}>
          {t('team.link.done')}
        </Button>
      </div>
      <Alert variant="info" title={t('team.link.note')} />
      <div className="flex flex-col gap-1.5">
        <div className="flex items-center justify-between">
          <span className="text-xs font-medium text-foreground-secondary">
            {t('team.link.linkLabel')}
          </span>
          <button
            type="button"
            onClick={copy}
            className={`text-xs font-medium hover:underline ${
              copyState === 'failed' ? 'text-status-danger' : 'text-action-primary'
            }`}
          >
            {copyState === 'copied'
              ? t('team.link.copied')
              : copyState === 'failed'
                ? t('team.link.copyFailed')
                : t('team.link.copy')}
          </button>
        </div>
        <code className="block overflow-x-auto rounded-md border border-border-default bg-background-default px-3 py-2 font-mono text-xs text-foreground-primary">
          {invitation.inviteLink}
        </code>
      </div>
    </div>
  )
}
